import React, { useState, useRef, useEffect } from "react";
import moment from "moment";
import { useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";

import AttachFileIcon from "@material-ui/icons/AttachFile";
import Button from "designV2/components/Button";

const SEND_MESSAGE = gql`
  mutation sendMessage($conversation: ID!, $messageType: MessageType!, $text: String) {
    sendMessage(sendMessageInput: { conversation: $conversation, messageType: $messageType, text: $text }) {
      _id
      text
    }
  }
`;

const IMAGE_EXTENSIONS = /\.(png|jpe?g|gif|webp|svg)$/i;

const readableError = (error) => {
  if (!error) return "";
  if (error.graphQLErrors && error.graphQLErrors.length > 0) {
    return error.graphQLErrors[0].message;
  }
  return error.message || "Something went wrong, please try again.";
};

const renderMessageBody = (message) => {
  if (message.messageType === "Upload") {
    return (
      <a href={message.text} target="_blank" rel="noopener noreferrer" className="conversationAttachment">
        {IMAGE_EXTENSIONS.test(message.text) ? (
          <img src={message.text} alt="attachment" className="conversationAttachmentImage" />
        ) : (
          <span className="conversationAttachmentLink">
            <AttachFileIcon style={{ fontSize: 16 }} /> View attachment
          </span>
        )}
      </a>
    );
  }

  if (message.messageType === "Object" && typeof message.text === "string") {
    try {
      const parsed = JSON.parse(message.text);
      return (
        <div className="conversationObjectMessage">
          {Object.keys(parsed).map((key) => (
            <div key={key} className="conversationObjectRow">
              <span className="conversationObjectKey">{key}</span>
              <span>{String(parsed[key])}</span>
            </div>
          ))}
        </div>
      );
    } catch (e) {
      return message.text;
    }
  }

  return message.text;
};

/**
 * Reply - the body of a conversation dialog: the message thread plus a
 * composer. Rendered inside a DialogShell by ConversationsList, which owns
 * the title/subtitle/close button, so this component is just the content.
 *
 * Sending a message appends it to the thread locally instead of closing
 * the dialog — the pre-redesign version closed the dialog the moment a
 * reply was sent, which meant you had to reopen the same conversation to
 * see it land or to send a follow-up.
 */
const Reply = ({ conversation, readOnly, onSent }) => {
  const bottomRef = useRef(null);
  const [messages, setMessages] = useState(conversation.messages || []);
  const [text, setText] = useState("");
  const [sendError, setSendError] = useState("");

  const [sendMessage, { loading: sending }] = useMutation(SEND_MESSAGE);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView();
  }, [messages.length]);

  const onSend = () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;

    setSendError("");
    sendMessage({
      variables: {
        conversation: conversation._id,
        messageType: "Text",
        text: trimmed,
      },
    })
      .then((res) => {
        setMessages((current) => [
          ...current,
          {
            _id: res.data.sendMessage._id,
            text: res.data.sendMessage.text,
            senderType: "Admin",
            messageType: "Text",
            createdAt: new Date().toISOString(),
          },
        ]);
        setText("");
        // The list's unread count and last-message preview live on the
        // parent query, so it refreshes quietly in the background — the
        // dialog itself stays open on the thread that was just updated.
        if (onSent) onSent();
      })
      .catch((err) => setSendError(readableError(err)));
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" || e.charCode === 13) {
      e.preventDefault();
      onSend();
    }
  };

  const sortedMessages = [...messages].sort((a, b) => (b.createdAt > a.createdAt ? -1 : 1));

  return (
    <div className="conversationThread">
      <div className="conversationChat">
        {sortedMessages.map((message) => {
          const isAdmin = message.senderType === "Admin";
          const senderName =
            (message.user && message.user.fullName) || (message.driver && message.driver.fullName) || null;

          return (
            <div key={message._id} className={`conversationBubbleRow ${isAdmin ? "isAdmin" : "isOther"}`}>
              <div className="conversationSenderLabel">
                {message.senderType}
                {senderName && ` · ${senderName}`}
              </div>
              <div className="conversationBubble">
                {renderMessageBody(message)}
                <span className="conversationBubbleTime">{moment(message.createdAt).format("MMM D, hh:mm A")}</span>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {sendError && (
        <div className="dsFormMessage error" role="alert">
          {sendError}
        </div>
      )}

      {!readOnly && (
        <div className="conversationComposer">
          <input
            type="text"
            className="dsInput"
            value={text}
            placeholder="Type your message..."
            onChange={(e) => setText(e.target.value)}
            onKeyPress={handleKeyPress}
          />
          <Button variant="primary" onClick={onSend} disabled={!text.trim()} loading={sending}>
            Send
          </Button>
        </div>
      )}
    </div>
  );
};

export default Reply;
